import { useEffect, useState } from "react";
import { Phone, MessageSquare, Mail, Zap, Check } from "lucide-react";

/**
 * CRM & Automation hero visual: every channel feeding one AI brain.
 * Five channels (missed call, SMS, WhatsApp, email, Instagram) pulse into a
 * central AI node, which fires a cycling auto-reply. Sample data, labeled Demo.
 * Motion: SMIL dots along SVG paths + CSS pulses; React only cycles the reply
 * text (paused for prerender / reduced motion).
 */

const prefersStatic = () =>
  (typeof navigator !== "undefined" && navigator.webdriver === true) ||
  (typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);

const REPLIES = [
  { channel: "Missed call", text: "Texted back in 8s. Booked for 4 PM" },
  { channel: "WhatsApp", text: "Quote sent. Follow-up scheduled" },
  { channel: "Email", text: "Replied with pricing. Tagged hot lead" },
  { channel: "Instagram", text: "DM answered. Call booked Friday" },
  { channel: "SMS", text: "Reschedule handled. Reminder set" },
];

/* Simplified brand glyphs (lucide carries no brand icons) */
const WhatsAppGlyph = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden>
    <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm0 2a8 8 0 1 1-4.2 14.8l-.5-.3-2.5.7.7-2.5-.3-.5A8 8 0 0 1 12 4Zm-3 4.2c-.2 0-.5 0-.7.3-.9 1-.7 2.5.1 3.8 1 1.5 2.4 2.8 4.2 3.5 1.4.6 2.6.6 3.4-.3.3-.3.5-.9.4-1.2-.1-.3-1.6-.9-1.9-1-.3-.1-.5 0-.7.3l-.4.5c-.1.2-.3.2-.6.1a6.4 6.4 0 0 1-2.9-2.5c-.2-.3-.1-.5.1-.6l.4-.5c.2-.2.2-.4.1-.6l-.8-1.8c-.2-.4-.4-.4-.7-.4Z" />
  </svg>
);
const InstagramGlyph = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <rect x="3" y="3" width="18" height="18" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.2" cy="6.8" r="1.1" fill="currentColor" stroke="none" />
  </svg>
);

const CHANNELS = [
  { label: "Missed call", icon: <Phone className="w-4 h-4" />, chip: "bg-rose-50 text-rose-600 border-rose-100", y: 46 },
  { label: "SMS", icon: <MessageSquare className="w-4 h-4" />, chip: "bg-blue-50 text-blue-600 border-blue-100", y: 96 },
  { label: "WhatsApp", icon: <WhatsAppGlyph />, chip: "bg-emerald-50 text-emerald-600 border-emerald-100", y: 146 },
  { label: "Email", icon: <Mail className="w-4 h-4" />, chip: "bg-amber-50 text-amber-600 border-amber-100", y: 196 },
  { label: "Instagram", icon: <InstagramGlyph />, chip: "bg-fuchsia-50 text-fuchsia-600 border-fuchsia-100", y: 246 },
];

const NODE = { x: 300, y: 146 };

export const OmnichannelVisual = () => {
  const [replyIdx, setReplyIdx] = useState(0);
  useEffect(() => {
    if (prefersStatic()) return;
    const id = window.setInterval(() => setReplyIdx((i) => (i + 1) % REPLIES.length), 3600);
    return () => window.clearInterval(id);
  }, []);
  const reply = REPLIES[replyIdx];

  return (
    <div className="relative mx-auto w-full max-w-[500px] select-none">
      <style>{`
        @keyframes ocv-ring { 0% { transform: scale(1); opacity: 0.5; } 100% { transform: scale(1.9); opacity: 0; } }
        .ocv-ring { animation: ocv-ring 2.4s cubic-bezier(0.16,1,0.3,1) infinite; }
        @keyframes ocv-reply-in { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .ocv-reply { animation: ocv-reply-in 0.45s cubic-bezier(0.16,1,0.3,1) both; }
        @media (prefers-reduced-motion: reduce) {
          .ocv-ring, .ocv-reply { animation: none; }
          .ocv-dots { display: none; }
        }
      `}</style>

      {/* soft glow behind the card */}
      <div className="absolute -inset-8 -z-10 bg-[radial-gradient(circle_at_50%_40%,rgba(47,95,232,0.28),transparent_65%)] blur-2xl" />

      <div className="relative overflow-hidden rounded-[1.5rem] border border-white/10 bg-white shadow-[0_24px_60px_-20px_rgba(0,0,0,0.5)]">
        {/* header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5">
          <div>
            <div className="text-sm font-bold tracking-tight text-slate-900">Every channel. One brain.</div>
            <div className="text-[11px] font-medium text-slate-500">AI answers wherever the lead lands</div>
          </div>
          <span className="inline-flex items-center gap-1.5 font-mono-ibm rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.1em] text-slate-600">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            Demo
          </span>
        </div>

        {/* canvas */}
        <div className="relative px-4 pt-4 pb-3">
          <svg viewBox="0 0 460 292" className="block w-full" aria-hidden>
            {CHANNELS.map((c, i) => (
              <path key={c.label} id={`ocv-p${i}`} d={`M128 ${c.y} C 210 ${c.y}, 225 ${NODE.y}, ${NODE.x - 26} ${NODE.y}`} fill="none" stroke="#E2E8F0" strokeWidth="1.5" />
            ))}
            <path id="ocv-out" d={`M${NODE.x + 26} ${NODE.y} C 380 ${NODE.y}, 390 ${NODE.y}, 436 ${NODE.y}`} fill="none" stroke="#CBD5E1" strokeWidth="1.5" strokeDasharray="3 4" />
            <g className="ocv-dots">
              {CHANNELS.map((c, i) => (
                <circle key={c.label} r="3.2" fill="#2F5FE8">
                  <animateMotion dur="3.6s" begin={`${i * 0.72}s`} repeatCount="indefinite" keyPoints="0;1" keyTimes="0;1">
                    <mpath href={`#ocv-p${i}`} />
                  </animateMotion>
                  <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.1;0.85;1" dur="3.6s" begin={`${i * 0.72}s`} repeatCount="indefinite" />
                </circle>
              ))}
              <circle r="3.2" fill="#10B981">
                <animateMotion dur="1.8s" begin="0.9s" repeatCount="indefinite">
                  <mpath href="#ocv-out" />
                </animateMotion>
              </circle>
            </g>
          </svg>

          {/* channel chips (positioned over the svg rows) */}
          <div className="absolute inset-0 px-4 pt-4">
            {CHANNELS.map((c) => (
              <div key={c.label} className="absolute left-4 flex items-center gap-2.5" style={{ top: `${(c.y / 292) * 100}%`, transform: "translateY(-50%)" }}>
                <span className={`grid h-9 w-9 place-items-center rounded-xl border ${c.chip}`}>{c.icon}</span>
                <span className="text-[12px] font-bold text-slate-700">{c.label}</span>
              </div>
            ))}

            {/* AI node */}
            <div className="absolute" style={{ left: `${(NODE.x / 460) * 100}%`, top: `${(NODE.y / 292) * 100}%`, transform: "translate(-50%, -50%)" }}>
              <span className="ocv-ring absolute inset-0 rounded-2xl bg-[#2F5FE8]/40" />
              <span className="relative grid h-14 w-14 place-items-center rounded-2xl bg-[#101319] text-white shadow-[0_10px_30px_-8px_rgba(16,19,25,0.6)]">
                <Zap className="h-5 w-5" fill="currentColor" />
              </span>
              <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap font-mono-ibm text-[9px] font-bold uppercase tracking-[0.12em] text-slate-500">
                Frayze AI
              </span>
            </div>
          </div>
        </div>

        {/* cycling auto-reply */}
        <div className="border-t border-slate-100 bg-slate-50/70 px-5 py-3.5">
          <div key={replyIdx} className="ocv-reply flex items-center gap-3">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-emerald-500 text-white">
              <Check className="h-4 w-4" strokeWidth={3} />
            </span>
            <div className="min-w-0">
              <div className="truncate text-[13px] font-bold text-slate-900">{reply.text}</div>
              <div className="text-[11px] font-medium text-slate-500">{reply.channel} · handled automatically</div>
            </div>
          </div>
        </div>
      </div>

      {/* floating accent */}
      <div className="absolute -top-4 -right-3 z-10 rounded-full bg-[#2F5FE8] px-3.5 py-1.5 text-xs font-bold text-white shadow-lg shadow-[#2F5FE8]/30">
        Replies in seconds, 24/7
      </div>
    </div>
  );
};

export default OmnichannelVisual;
